import { useState } from 'react';
// migration-tracker-disable-next-line forms -- CreateMetricModal's MetricForm is built on react-hook-form; this field integrates with the shared form context until the modal migrates to @gonfalon/forms
import type { UseFormReturn } from 'react-hook-form';
// migration-tracker-disable-next-line forms -- CreateMetricModal's MetricForm is built on react-hook-form; this field integrates with the shared form context until the modal migrates to @gonfalon/forms
import { Controller } from 'react-hook-form';
import { msg } from '@gonfalon/messages/msg';
import {
  DEFAULT_EVENT_LEVEL_PERCENTILE,
  EVENT_LEVEL_PERCENTILE_PRESETS,
  isEventLevelPercentilePreset,
  parseEventLevelPercentileInput,
  validateEventLevelPercentile,
} from '@gonfalon/metrics/eventLevelPercentile';
import { Button, ButtonGroup, FieldError, Group, Input, TextField } from '@launchpad-ui/components';

import type { MetricForm } from '../../../CreateMetricModal/formUtils';
import { PERCENTILE_LEVEL_LABEL_ID } from '../labels';

import styles from './PercentileLevelField.module.css';

export const PercentileLevelField: React.FC<{ context: UseFormReturn<MetricForm> }> = ({ context }) => {
  const { control, getValues, setValue } = context;
  const [isCustom, setIsCustom] = useState(() => {
    const initial = getValues('percentileValue');
    return initial !== undefined && !isEventLevelPercentilePreset(initial);
  });
  const [customInput, setCustomInput] = useState(() => {
    const initial = getValues('percentileValue');
    return initial !== undefined && !isEventLevelPercentilePreset(initial) ? String(initial) : '';
  });
  const [shouldAutoFocusCustom, setShouldAutoFocusCustom] = useState(false);

  return (
    <Controller
      control={control}
      name="percentileValue"
      rules={{ validate: validateEventLevelPercentile }}
      shouldUnregister
      render={({ field, fieldState }) => {
        const selectedPreset = isCustom ? undefined : (field.value ?? DEFAULT_EVENT_LEVEL_PERCENTILE);

        return (
          <div className={styles.percentileLevel}>
            <ButtonGroup spacing="compact" aria-labelledby={PERCENTILE_LEVEL_LABEL_ID}>
              {EVENT_LEVEL_PERCENTILE_PRESETS.map((preset) => {
                const isSelected = preset === selectedPreset;
                return (
                  <Button
                    key={preset}
                    className={styles.button}
                    aria-pressed={isSelected}
                    data-selected={isSelected || undefined}
                    onPress={() => {
                      setIsCustom(false);
                      setCustomInput('');
                      setValue('percentileValue', preset, { shouldDirty: true, shouldValidate: true });
                    }}
                  >
                    {msg('metrics.definitionSummary.percentile', { value: preset })}
                  </Button>
                );
              })}
              <Button
                className={styles.button}
                aria-pressed={isCustom}
                data-selected={isCustom || undefined}
                onPress={() => {
                  if (isCustom) {
                    return;
                  }
                  const seed = String(field.value ?? DEFAULT_EVENT_LEVEL_PERCENTILE);
                  setIsCustom(true);
                  setShouldAutoFocusCustom(true);
                  setCustomInput(seed);
                  setValue('percentileValue', parseEventLevelPercentileInput(seed), {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
              >
                {msg('metrics.metricDefinition.percentileLevelCustom')}
              </Button>
            </ButtonGroup>
            {isCustom && (
              <>
                <div className={styles.connector} />
                <TextField
                  aria-label={msg('metrics.metricDefinition.percentileLevelCustomLabel')}
                  value={customInput}
                  isInvalid={Boolean(fieldState.error)}
                  autoFocus={shouldAutoFocusCustom}
                  className={styles.customField}
                  onChange={(input) => {
                    setCustomInput(input);
                    setValue('percentileValue', parseEventLevelPercentileInput(input), {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  onBlur={field.onBlur}
                >
                  <Group className={styles.customInputGroup}>
                    <div className={styles.customInputPrefix} aria-hidden>
                      {msg('metrics.metricDefinition.percentileLevelCustomPrefix')}
                    </div>
                    <Input inputMode="numeric" className={styles.customInput} data-test-id="custom-percentile-input" />
                  </Group>
                  {fieldState.error && <FieldError className={styles.error}>{fieldState.error.message}</FieldError>}
                </TextField>
              </>
            )}
          </div>
        );
      }}
    />
  );
};
